/**
 * Global axios auth refresh — covers raw axios.get/post used across the app
 * (apiService has its own instance and uses the same shared refresh helper).
 *
 * Also refreshes proactively when the tab becomes visible after idle.
 */
import axios from 'axios';
import {
    ensureFreshAccessToken,
    forceSignOutToSignIn,
    isAuthRefreshUrl,
    refreshAccessToken,
} from '../shared/auth/tokenRefresh';

let installed = false;

function attachAuthHeader(config, accessToken) {
    if (!accessToken || !config) return;
    config.headers = config.headers || {};
    if (typeof config.headers.set === 'function') {
        config.headers.set('Authorization', `Bearer ${accessToken}`);
    } else {
        config.headers.Authorization = `Bearer ${accessToken}`;
    }
}

export function installAxiosAuthRefresh() {
    if (installed) return;
    installed = true;

    axios.interceptors.request.use(async (config) => {
        if (config?.skipAuthRefresh || isAuthRefreshUrl(config.url)) {
            return config;
        }
        const auth = config.headers?.Authorization || config.headers?.authorization;
        const hasBearer = typeof auth === 'string' && /^Bearer\s+/i.test(auth);
        if (!hasBearer) return config;

        try {
            const access = await ensureFreshAccessToken();
            if (access) attachAuthHeader(config, access);
        } catch {
            /* proceed; response interceptor may still recover */
        }
        return config;
    });

    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const config = error.config;
            const status = error.response?.status;
            const authHeader = config?.headers?.Authorization || config?.headers?.authorization;
            const hadBearer = typeof authHeader === 'string' && /^Bearer\s+/i.test(authHeader);

            if (
                status !== 401
                || !config
                || !hadBearer
                || config.skipAuthRefresh
                || config._authRetry
                || isAuthRefreshUrl(config.url)
            ) {
                return Promise.reject(error);
            }

            config._authRetry = true;
            const access = await refreshAccessToken();
            if (!access) {
                const hadSession = sessionStorage.getItem('token') || sessionStorage.getItem('refreshToken');
                if (hadSession) forceSignOutToSignIn();
                return Promise.reject(error);
            }

            attachAuthHeader(config, access);
            return axios(config);
        }
    );

    if (typeof window !== 'undefined') {
        const onResume = () => {
            if (sessionStorage.getItem('refreshToken')) {
                ensureFreshAccessToken().catch(() => {});
            }
        };
        window.addEventListener('focus', onResume);
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') onResume();
        });
    }
}

export default installAxiosAuthRefresh;
